import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Checkbox, FormControl, FormLabel, Grid, GridItem, Heading, Input, NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, Radio, RadioGroup, Stack, Tooltip } from '@chakra-ui/react'
import { getMowCurbPricingConfig } from 'api'
import { useAppContext } from 'hooks/useAppContext'

// Installation option IDs that support mow curb
const PROFESSIONAL_INSTALLATION_IDS_WITH_MOW_CURB = [2, 3, 4, 7, 8, 9]

// Helper function to check if an installation option supports mow curb
export const doesInstallationOptionSupportMowCurb = (installationOptionId) => {
  return installationOptionId && PROFESSIONAL_INSTALLATION_IDS_WITH_MOW_CURB.includes(installationOptionId)
}

export const EdgeMowCurbForm = ({ form, setForm, ...rest }) => {
  const { createToast } = useAppContext()
  const [mowCurbConfig, setMowCurbConfig] = useState({
    mowCurbBase: 200,
    mowCurbPerFoot: 50
  })

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getMowCurbPricingConfig()
        setMowCurbConfig(res)
      } catch (err) {
        createToast('error', 'Error', err.response?.data || 'Failed to load mow curb config')
      }
    }
    load()
  }, [])

  // Calculate mow curb cost
  useEffect(() => {
    if (!form.mowCurbEnabled) {
      setForm(draft => {
        draft.mowCurbCost = 0
        draft.mowCurbWidthFt = 0
        draft.mowCurbCalculationType = ''
      })
      return
    }

    // If pedestal option is selected but no pedestal is available, reset to manual
    if (form.mowCurbCalculationType === 'pedestal' && form.noPedestal) {
      setForm(draft => {
        draft.mowCurbCalculationType = 'manual'
        draft.mowCurbWidthFt = 0
      })
      return
    }

    let widthInFeet = 0

    if (form.mowCurbCalculationType === 'pedestal') {
      // Option 1: Based on pedestal width
      if (!form.noPedestal && form.pedestalLengthIn > 0) {
        // Pedestal width + 12 inches, then convert to feet
        const widthInInches = form.pedestalLengthIn + 12
        widthInFeet = widthInInches / 12
      } else {
        // No pedestal selected, can't calculate
        setForm(draft => {
          draft.mowCurbCost = 0
        })
        return
      }
    } else if (form.mowCurbCalculationType === 'manual') {
      // Option 2: Manual entry
      widthInFeet = form.mowCurbWidthFt || 0
    }

    // Calculate cost: base + (width in feet * price per foot)
    const cost = mowCurbConfig.mowCurbBase + (widthInFeet * mowCurbConfig.mowCurbPerFoot)

    setForm(draft => {
      draft.mowCurbWidthFt = widthInFeet
      draft.mowCurbCost = parseFloat(cost.toFixed(2))
    })
  }, [
    form.mowCurbEnabled,
    form.mowCurbCalculationType,
    form.noPedestal,
    form.pedestalLengthIn,
    form.mowCurbWidthFt,
    mowCurbConfig.mowCurbBase,
    mowCurbConfig.mowCurbPerFoot
  ])

  // Check if installation option requires mow curb
  const shouldShowMowCurb = doesInstallationOptionSupportMowCurb(form.installationOption?.id)

  if (!shouldShowMowCurb) {
    return null
  }

  return (
    <Box {...rest}>
      <Grid
        templateColumns="5fr 1fr"
        rowGap={5}
        columnGap={10}
        w="900px"
        maxW="100%"
      >
        <GridItem>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Heading size="md">Mow Curb</Heading>
            <Checkbox
              borderColor="gray.400"
              isChecked={!form.mowCurbEnabled}
              onChange={(e) => setForm(draft => {
                draft.mowCurbEnabled = !e.target.checked
                if (!draft.mowCurbEnabled) {
                  draft.mowCurbCalculationType = ''
                  draft.mowCurbWidthFt = 0
                  draft.mowCurbCost = 0
                }
              })}
            >
              No Mow Curb
            </Checkbox>
          </Box>
        </GridItem>
        <GridItem></GridItem>
        {form.mowCurbEnabled ? (
          <>
            <GridItem colSpan={2}>
              <FormControl isRequired>
                <FormLabel>Calculation Method</FormLabel>
                <RadioGroup
                  value={form.mowCurbCalculationType}
                  onChange={(value) => setForm(draft => {
                    draft.mowCurbCalculationType = value
                    if (value === 'pedestal') {
                      draft.mowCurbWidthFt = 0 // Will be calculated
                    } else {
                      draft.mowCurbWidthFt = 0 // User will enter
                    }
                  })}
                >
                  <Stack direction="row" spacing={4}>
                    {!form.noPedestal && (
                      <Radio value="pedestal">
                        Based on Pedestal Width
                      </Radio>
                    )}
                    <Radio value="manual">Manual Entry</Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>
            </GridItem>
            {form.mowCurbCalculationType === 'pedestal' && (
              <GridItem colSpan={2}>
                <FormControl>
                  <FormLabel>Mow Curb Width (ft)</FormLabel>
                  <Input
                    value={form.mowCurbWidthFt > 0 ? form.mowCurbWidthFt.toFixed(2) : '0.00'}
                    readOnly
                    bg="gray.100"
                  />
                  {form.pedestalLengthIn > 0 ? (
                    <FormLabel fontSize="xs" color="gray.600" mt={1}>
                      Calculated: Pedestal width ({form.pedestalLengthIn}&quot;) + 12&quot; = {((form.pedestalLengthIn + 12) / 12).toFixed(2)} ft
                    </FormLabel>
                  ) : (
                    <FormLabel fontSize="xs" color="red.500" mt={1}>
                      Please select a pedestal first
                    </FormLabel>
                  )}
                </FormControl>
              </GridItem>
            )}
            {form.mowCurbCalculationType === 'manual' && (
              <GridItem colSpan={2}>
                <FormControl isRequired>
                  <Tooltip label="Mow curbs should be the width of the sign or pedestal plus 2 feet" placement="top">
                    <FormLabel>
                      Mow Curb Width (ft)
                    </FormLabel>
                  </Tooltip>
                  <NumberInput
                    onChange={(valueString, valueNumber) => setForm(draft => {
                      draft.mowCurbWidthFt = valueNumber || 0
                    })}
                    value={form.mowCurbWidthFt}
                    min={0}
                    precision={2}
                    step={0.5}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </GridItem>
            )}
            <GridItem display="flex" alignItems="flex-end">
              <FormControl>
                <FormLabel>Mow Curb Cost</FormLabel>
                <Input name="mow-curb-cost" value={form.mowCurbCost} readOnly />
              </FormControl>
            </GridItem>
          </>
        ) : null}
      </Grid>
    </Box>
  )
}

EdgeMowCurbForm.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func
}
